import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Modal, ModalContent, Input, Listbox, ListboxItem, ListboxSection } from '@heroui/react';
import { useUI } from '@/contexts/UIContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut';
import { apiClient } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { navFlat, isNavItemVisible } from '@/config/navigation';
import { usePermission } from '@/hooks/usePermission';
import { usePlatformCapabilities } from '@/features/platform/hooks';
import { MagnifyingGlassIcon, GlobeAltIcon, InboxIcon } from '@/icons';
import type { PaginatedResult } from '@/types/api';

interface DomainHit { id: string; domain: string }
interface MailboxHit { id: string; address: string }

/**
 * Command palette (Cmd/Ctrl-K). Navigation is searched client-side over the nav
 * model; domains + mailboxes are searched live against the REAL list endpoints
 * (debounced). Selecting any result navigates to it.
 */
export function CommandPalette() {
  const { commandOpen, setCommandOpen } = useUI();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const debounced = useDebouncedValue(term.trim(), 250);

  useKeyboardShortcut('k', () => setCommandOpen(true), { meta: true });

  const { can } = usePermission();
  const { canOnboard } = usePlatformCapabilities();
  // Same visibility rule as the sidebar: no entries for pages the user can't use or that aren't planned.
  const navMatches = useMemo(() => {
    const q = debounced.toLowerCase();
    const visible = navFlat.filter((n) => isNavItemVisible(n, { can, canOnboard }));
    if (!q) return visible;
    return visible.filter((n) => n.label.toLowerCase().includes(q));
  }, [debounced, can, canOnboard]);

  const { data: domainHits } = useQuery({
    queryKey: [...queryKeys.search(debounced), 'domains'],
    queryFn: async () =>
      (await apiClient.get<PaginatedResult<DomainHit>>('/domains', { params: { q: debounced, pageSize: 5 } })).data,
    enabled: commandOpen && debounced.length >= 2,
    staleTime: 15_000,
  });

  const { data: mailboxHits } = useQuery({
    queryKey: [...queryKeys.search(debounced), 'mailboxes'],
    queryFn: async () =>
      (await apiClient.get<PaginatedResult<MailboxHit>>('/mailboxes', { params: { q: debounced, pageSize: 5 } })).data,
    enabled: commandOpen && debounced.length >= 2,
    staleTime: 15_000,
  });

  const go = (to: string) => {
    setCommandOpen(false);
    setTerm('');
    navigate(to);
  };

  return (
    <Modal
      isOpen={commandOpen}
      onOpenChange={setCommandOpen}
      placement="top"
      size="xl"
      hideCloseButton
      backdrop="blur"
      classNames={{ base: 'bg-surface', wrapper: 'items-start pt-[10vh]' }}
    >
      <ModalContent>
        <div className="p-2">
          <Input
            autoFocus
            value={term}
            onValueChange={setTerm}
            placeholder="Search navigation, domains, mailboxes…"
            startContent={<MagnifyingGlassIcon className="h-5 w-5 text-fg-subtle" />}
            variant="flat"
            size="lg"
            aria-label="Command palette search"
          />
          <div className="mt-2 max-h-[50vh] overflow-y-auto">
            <Listbox aria-label="Search results" onAction={(key) => go(String(key))}>
              <ListboxSection title="Navigation">
                {navMatches.map((n) => (
                  <ListboxItem key={n.to} startContent={<n.icon className="h-4 w-4 text-fg-subtle" />}>
                    {n.label}
                  </ListboxItem>
                ))}
              </ListboxSection>
              {(domainHits?.data?.length ?? 0) > 0 ? (
                <ListboxSection title="Domains">
                  {(domainHits?.data ?? []).map((d) => (
                    <ListboxItem key={`/domains/${d.id}`} startContent={<GlobeAltIcon className="h-4 w-4 text-fg-subtle" />}>
                      {d.domain}
                    </ListboxItem>
                  ))}
                </ListboxSection>
              ) : null}
              {(mailboxHits?.data?.length ?? 0) > 0 ? (
                <ListboxSection title="Mailboxes">
                  {(mailboxHits?.data ?? []).map((m) => (
                    <ListboxItem key={`/mailboxes/${m.id}`} startContent={<InboxIcon className="h-4 w-4 text-fg-subtle" />}>
                      {m.address}
                    </ListboxItem>
                  ))}
                </ListboxSection>
              ) : null}
            </Listbox>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
